import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AlertDialogConfig } from '../alert/alert-dialog.component';
import { Store } from '@ngrx/store';
import { ErrorState } from '../store/states/error.state';
import { Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ErrorNotificationService {
  private alertSubject = new Subject<AlertDialogConfig>();
  private closeSubject = new Subject<boolean>();
  alertState = this.alertSubject.asObservable();
  closeState = this.closeSubject.asObservable();
  private confirmResolver?: (value: boolean) => void;
  private toastSubject = new Subject<AlertDialogConfig>();
  toast$ = this.toastSubject.asObservable();

  constructor(private store: Store<ErrorState>) {
    // La souscription au store est supprimée car ErrorEffects gère déjà l'affichage des erreurs
    // via globalError -> showToast.
    // Cela évite l'affichage en double des toasters.
  }

  /**
   * Normalise une erreur : transforme un HttpErrorResponse brut en objet d'erreur formaté
   */
  private normalizeError(error: any): any {
    // Si c'est déjà un objet d'erreur formaté (avec code, status, message, title, type), le retourner tel quel
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      'message' in error &&
      'title' in error
    ) {
      return error;
    }

    // Si c'est un HttpErrorResponse brut, le transformer
    if (
      error instanceof HttpErrorResponse ||
      (error && error.status && error.message && error.url)
    ) {
      console.log(
        '%c[DEBUG][error-notification.service] Normalizing HttpErrorResponse:',
        'color: orange;',
        error,
      );

      // Extraire le message du corps de la réponse
      const formatErrorMessage = (err: any): string => {
        if (typeof err === 'string') {
          if (err.includes('Http failure response for')) {
            return `Erreur ${error.status}: ${error.statusText || 'Erreur de communication'}`;
          }
          return err;
        }
        if (err?.detail && typeof err.detail === 'string') {
          return err.detail;
        }
        if (err?.message && typeof err.message === 'string') {
          return err.message;
        }
        if (err?.details && typeof err.details === 'string') {
          return err.details;
        }
        if (typeof err === 'object' && err !== null) {
          try {
            const jsonStr = JSON.stringify(err);
            if (jsonStr.length > 200) {
              return 'Erreur technique - Détails disponibles dans la console';
            }
            return jsonStr;
          } catch (_e) {
            return 'Erreur inconnue';
          }
        }
        return error.statusText || 'Erreur inconnue';
      };

      return {
        code: error.status || 999,
        status: error.status,
        message: formatErrorMessage(error.error || error),
        details: error.error?.detail || [],
        type: 'error',
        title: error.statusText || 'Erreur',
      };
    }

    // Sinon, créer un objet d'erreur par défaut
    return {
      code: error?.code || error?.status || 999,
      status: error?.status || error?.code || 999,
      message: this.formatErrorMessage(error?.message || error),
      details: error?.details || [],
      type: error?.type || 'error',
      title: error?.title || error?.statusText || 'Erreur',
    };
  }

  /**
   * Formate un message d'erreur de manière sûre
   */
  private formatErrorMessage(msg: any): string {
    if (typeof msg === 'string') {
      // Filtrer les messages génériques d'Angular HttpClient
      if (msg.includes('Http failure response for')) {
        return 'Erreur de communication avec le serveur';
      }
      return msg;
    }
    if (typeof msg === 'object' && msg !== null) {
      try {
        const jsonStr = JSON.stringify(msg);
        // Si le JSON est trop long, retourner un message générique
        if (jsonStr.length > 200) {
          return 'Erreur technique - Détails disponibles dans la console';
        }
        return jsonStr;
      } catch (_e) {
        return 'Erreur inconnue';
      }
    }
    return String(msg || 'Erreur inconnue');
  }

  showError(config: AlertDialogConfig): void {
    if (!config.isConfirm) {
      this.showClarityToast(config);
      return;
    }

    // Cas où une interaction utilisateur est attendue (modale)
    this.alertSubject.next(config);
  }

  private showClarityToast(config: AlertDialogConfig): void {
    this.toastSubject.next(config);
  }

  showSuccess(title: string, message: string): void {
    const config: AlertDialogConfig = {
      title,
      message,
      confirmLabel: 'OK',
      type: 'info',
    };
    this.showError(config);
  }

  showWarning(title: string, message: string): void {
    const config: AlertDialogConfig = {
      title,
      message,
      confirmLabel: 'Fermer',
      type: 'warning',
    };
    this.showError(config);
  }

  closeAlert(): void {
    this.closeSubject.next(false);
  }

  confirm(config: AlertDialogConfig): Observable<boolean> {
    this.alertSubject.next(config);
    return new Observable<boolean>(observer => {
      this.confirmResolver = (result: boolean) => {
        observer.next(result);
        observer.complete();
      };
    });
  }

  resolveConfirm(value: boolean): void {
    if (this.confirmResolver) {
      this.confirmResolver(value);
      this.confirmResolver = undefined;
    }
  }
}
